import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, Shield, User, X, Info, RefreshCw } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const API = window.location.origin + "/api";

const COULEURS_UTILISATEUR = [
  { value: "violet",  label: "Violet",  hex: "#8B5CF6" },
  { value: "vert",    label: "Vert",    hex: "#10B981" },
  { value: "orange",  label: "Orange",  hex: "#F97316" },
  { value: "rose",    label: "Rose",    hex: "#EC4899" },
  { value: "cyan",    label: "Cyan",    hex: "#06B6D4" },
  { value: "rouge",   label: "Rouge",   hex: "#EF4444" },
  { value: "bleu",    label: "Bleu",    hex: "#3B82F6" },
  { value: "jaune",   label: "Jaune",   hex: "#EAB308" },
  { value: "indigo",  label: "Indigo",  hex: "#6366F1" },
  { value: "teal",    label: "Teal",    hex: "#14B8A6" },
  { value: "marron",  label: "Marron",  hex: "#92400E" },
  { value: "gris",    label: "Gris",    hex: "#6B7280" },
];

function getUserColor(couleur) {
  return COULEURS_UTILISATEUR.find(c => c.value === couleur) || { hex: "#6366F1", label: couleur || "Indigo" };
}

const EMPTY_FORM = { email: "", name: "", password: "", role: "operateur", couleur: "violet" };

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  const [syncing, setSyncing] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/users/sync-azure`);
      alert(`Synchronisation terminée — ${data.total_users} utilisateur(s) au total`);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de la synchronisation");
    } finally {
      setSyncing(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/users`);
      setUsers(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const openAdd = () => {
    setEditUser(null);
    setForm(EMPTY_FORM);
    setError("");
    setModalOpen(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({ email: u.email, name: u.name, password: "", role: u.role, couleur: u.couleur || "blanc" });
    setError("");
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (editUser) {
        const body = { ...form };
        if (!body.password) delete body.password;
        await axios.put(`${API}/users/${editUser.id}`, body);
      } else {
        await axios.post(`${API}/users`, form);
      }
      setModalOpen(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/users/${id}`);
      setDeleteId(null);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  return (
    <div data-testid="users-page" style={{ fontFamily: "Manrope, sans-serif" }}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            Gestion des utilisateurs
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">{users.length} utilisateur{users.length !== 1 ? "s" : ""}</p>
        </div>
        <button onClick={handleSync} disabled={syncing}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Synchronisation..." : "Synchroniser Azure"}
        </button>
      </div>

      {/* Bannière d'information */}
      <div className="flex items-start gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 mb-6 text-sm text-indigo-800">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-indigo-500" />
        <div>
          <span className="font-semibold">Seuls les utilisateurs autorisés dans Azure apparaissent ici.</span>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Utilisateur</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Email</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Rôle</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Couleur</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">M365</th>
                <th className="px-5 py-3 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const userColor = getUserColor(u.couleur);
                return (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: userColor.hex + "22", border: `2px solid ${userColor.hex}` }}>
                          <span className="text-xs font-bold" style={{ color: userColor.hex }}>
                            {u.name?.[0]?.toUpperCase() || "?"}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{u.name}</p>
                          {u.id === currentUser?.id && (
                            <span className="text-xs text-indigo-500">Vous</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-slate-600">{u.email}</td>
                    <td className="px-5 py-4">
                      {/* Rôle modifiable en 1 clic */}
                      <button
                        onClick={async () => {
                          const newRole = u.role === "admin" ? "operateur" : "admin";
                          try {
                            await axios.put(`${API}/users/${u.id}`, { role: newRole });
                            fetchUsers();
                          } catch (err) {
                            alert(err.response?.data?.detail || "Erreur");
                          }
                        }}
                        title="Cliquer pour changer le rôle"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer hover:opacity-75 transition-opacity"
                        style={u.role === "admin"
                          ? { background: "#EEF2FF", color: "#4F46E5" }
                          : { background: "#F1F5F9", color: "#475569" }
                        }
                      >
                        {u.role === "admin" ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
                        {u.role === "admin" ? "Admin" : "Opérateur"}
                      </button>
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium border"
                        style={{ backgroundColor: userColor.hex + "18", borderColor: userColor.hex + "55", color: userColor.hex }}>
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: userColor.hex }} />
                        {userColor.label}
                      </span>
                    </td>
                    {/* Statut M365 */}
                    <td className="px-5 py-4">
                      {u.microsoft_id ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-50 text-green-700 border border-green-200 rounded-full text-xs font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                          Connecté
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-xs font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                          En attente
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5 justify-end">
                        <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}
                          className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        {u.id !== currentUser?.id && (
                          <button onClick={() => setDeleteId(u.id)} data-testid={`delete-user-${u.id}`}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm" onClick={() => setModalOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                {editUser ? "Modifier l'utilisateur" : "Nouvel utilisateur"}
              </h2>
              <button onClick={() => setModalOpen(false)} data-testid="user-modal-close"
                className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <form onSubmit={handleSave} className="px-6 py-5 space-y-4">
              {/* Nom — toujours modifiable */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Nom complet</label>
                <input type="text" value={form.name} onChange={e => setField("name", e.target.value)}
                  placeholder="Jean Dupont" required data-testid="user-field-name"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none" />
              </div>

              {/* Email — lecture seule en modification */}
              {editUser ? (
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Email Microsoft 365</label>
                  <div className="w-full px-3 py-2 rounded-lg border border-slate-100 bg-slate-50 text-sm text-slate-500">
                    {editUser.email || "(email non encore renseigné)"}
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Email Microsoft 365</label>
                  <input type="email" value={form.email} onChange={e => setField("email", e.target.value)}
                    placeholder="jean.dupont@siao42.fr" required data-testid="user-field-email"
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none" />
                </div>
              )}

              {/* Rôle */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Rôle</label>
                <select value={form.role} onChange={e => setField("role", e.target.value)}
                  data-testid="user-field-role"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 outline-none bg-white">
                  <option value="operateur">Opérateur</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>

              {/* Couleur personnelle */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  Couleur personnelle
                </label>
                <div className="grid grid-cols-6 gap-2">
                  {COULEURS_UTILISATEUR.map(({ value, label, hex }) => (
                    <button key={value} type="button"
                      onClick={() => setField("couleur", value)}
                      data-testid={`user-couleur-${value}`}
                      title={label}
                      className={`relative w-full aspect-square rounded-xl transition-all flex items-center justify-center ${
                        form.couleur === value
                          ? "ring-2 ring-offset-2 scale-110 shadow-md"
                          : "hover:scale-105 hover:shadow-sm"
                      }`}
                      style={{
                        backgroundColor: hex,
                        ringColor: hex,
                      }}>
                      {form.couleur === value && (
                        <span className="text-white text-xs font-bold">✓</span>
                      )}
                    </button>
                  ))}
                </div>
                {form.couleur && (
                  <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-full inline-block"
                      style={{ backgroundColor: getUserColor(form.couleur).hex }} />
                    Couleur sélectionnée : <strong>{getUserColor(form.couleur).label}</strong>
                  </p>
                )}
              </div>

              {error && <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setModalOpen(false)}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  Annuler
                </button>
                <button type="submit" disabled={saving} data-testid="user-save-btn"
                  className="flex-1 px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                  {saving ? "Enregistrement..." : (editUser ? "Modifier" : "Créer")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm" onClick={() => setDeleteId(null)} />
          <div className="relative bg-white rounded-xl shadow-xl p-6 max-w-sm w-full">
            <h3 className="font-semibold text-slate-900 mb-2">Confirmer la suppression</h3>
            <p className="text-sm text-slate-500 mb-5">Cet utilisateur sera définitivement supprimé.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} data-testid="cancel-delete-user-btn"
                className="flex-1 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50">
                Annuler
              </button>
              <button onClick={() => handleDelete(deleteId)} data-testid="confirm-delete-user-btn"
                className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700">
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
